/**
 * Nombres en español de los tipos de email y de los estados de envío del historial.
 *
 * Es un diccionario de etiquetas: no define qué opciones ofrece un filtro —esas se derivan de
 * los valores que la base tiene registrados— y un valor sin entrada acá se muestra crudo.
 */

export const TIPO_EMAIL_LABELS: Record<string, string> = {
  estado_cuenta: 'Estado de cuenta',
  recordatorio_deuda: 'Recordatorio deuda',
  nuevo_cliente: 'Nuevo cliente',
  presupuesto_aceptado: 'Presupuesto aceptado',
  resumen_movimientos: 'Resumen movimientos',
  nuevo_movimiento: 'Nuevo movimiento',
  bienvenida_estudio: 'Bienvenida',
  contrato_cliente: 'Contrato',
  alertas_vencimientos: 'Alertas vencimientos',
  resumen_notion: 'Resumen Notion',
  presentado_cliente: 'Presentado',
  recordatorio_lote_deudas: 'Recordatorio lote',
  presupuesto_enviado: 'Presupuesto enviado',
  cobranza_aviso: 'Aviso de cobranza',
  cobranza_reporte: 'Reporte de cobranza',
  recibo_pago: 'Recibo de pago',
}

export const etiquetaTipoEmail = (tipo: string): string => TIPO_EMAIL_LABELS[tipo] ?? tipo

export type VarianteEstadoEmail = 'default' | 'secondary' | 'destructive' | 'outline'

export interface EstadoEmailLabel {
  label: string
  variant: VarianteEstadoEmail
}

export const ESTADO_EMAIL_LABELS: Record<string, EstadoEmailLabel> = {
  pending: { label: 'Sin confirmar', variant: 'outline' },
  sent: { label: 'Enviado', variant: 'secondary' },
  delivered: { label: 'Entregado', variant: 'default' },
  opened: { label: 'Abierto', variant: 'default' },
  bounced: { label: 'Rebotado', variant: 'destructive' },
  complained: { label: 'Queja', variant: 'destructive' },
  failed: { label: 'Error', variant: 'destructive' },
  manual: { label: 'Registrado manual', variant: 'outline' },
  blocked_nonprod: { label: 'Bloqueado (entorno no productivo)', variant: 'outline' },
  blocked_suppressed: { label: 'Bloqueado (destinatario suprimido)', variant: 'outline' },
  blocked_rate_limit: { label: 'Bloqueado (límite por hora)', variant: 'outline' },
}

export const etiquetaEstadoEmail = (status: string): EstadoEmailLabel =>
  ESTADO_EMAIL_LABELS[status] ?? { label: status, variant: 'outline' }

export interface OpcionFiltroEmail {
  valor: string
  label: string
}

/**
 * Arma las opciones de un filtro del historial a partir de los valores que la base registró.
 *
 * `seleccionado` se agrega aunque no esté entre los valores, así un filtro puesto conserva su
 * nombre mientras la consulta carga. Las opciones salen ordenadas por su etiqueta en español.
 */
export const opcionesDeFiltroEmail = (
  valores: string[] | undefined,
  seleccionado: string | undefined,
  etiquetar: (valor: string) => string,
): OpcionFiltroEmail[] => {
  const unicos = new Set(valores ?? [])
  if (seleccionado) unicos.add(seleccionado)
  return [...unicos]
    .map((valor) => ({ valor, label: etiquetar(valor) }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }))
}
